/**
 * Hook for loading and managing YC analysis data
 */

import { useState, useEffect, useMemo } from 'react'
import type { AnalysisData, Company, FilterState } from './types'

const DATA_URL = '/data/yc_analysis.json'

export function useAnalysisData() {
  const [data, setData] = useState<AnalysisData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true)
        const response = await fetch(DATA_URL)
        if (!response.ok) {
          throw new Error(`Failed to load data: ${response.status}`)
        }
        const json = await response.json()
        setData(json)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  return { data, loading, error }
}

export function useFilteredCompanies(
  companies: Company[],
  filters: FilterState
) {
  return useMemo(() => {
    let filtered = companies

    // Filter by agents only
    if (filters.showAgentsOnly) {
      filtered = filtered.filter((c) => c.is_agent)
    }

    // Filter by year range
    filtered = filtered.filter((c) => {
      if (!c.launched_at) return false
      const year = new Date(c.launched_at).getFullYear()
      return year >= filters.yearRange[0] && year <= filters.yearRange[1]
    })

    // Filter by domain
    if (filters.selectedDomain) {
      filtered = filtered.filter((c) => c.domain === filters.selectedDomain)
    }

    // Filter by subdomain
    if (filters.selectedSubdomain) {
      filtered = filtered.filter(
        (c) => c.subdomain === filters.selectedSubdomain
      )
    }

    // Filter by search term
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase()
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          c.desc.toLowerCase().includes(term) ||
          c.domain.toLowerCase().includes(term) ||
          c.subdomain.toLowerCase().includes(term)
      )
    }

    // Sort by launch date descending
    filtered.sort((a, b) => {
      const dateA = a.launched_at ? new Date(a.launched_at).getTime() : 0
      const dateB = b.launched_at ? new Date(b.launched_at).getTime() : 0
      return dateB - dateA
    })

    return filtered
  }, [companies, filters])
}

export function useComputedMetrics(
  companies: Company[],
  yearRange: [number, number],
  yoyTrends?: { Year: number; 'Total Companies': number; 'AI Companies': number; 'Agent Companies': number }[]
) {
  return useMemo(() => {
    // Filter companies by year range
    const inRange = companies.filter((c) => {
      if (!c.launched_at) return false
      const year = new Date(c.launched_at).getFullYear()
      return year >= yearRange[0] && year <= yearRange[1]
    })

    const agents = inRange.filter((c) => c.is_agent)

    // Compute year-over-year data from summary if available, otherwise from companies
    const yearData: Record<
      number,
      { total: number; agents: number; ai: number }
    > = {}

    if (yoyTrends) {
      // Use pre-computed totals from summary (includes all YC companies)
      yoyTrends.forEach((trend) => {
        if (trend.Year >= yearRange[0] && trend.Year <= yearRange[1]) {
          yearData[trend.Year] = {
            total: trend['Total Companies'],
            agents: trend['Agent Companies'],
            ai: trend['AI Companies'],
          }
        }
      })
    } else {
      // Fallback: compute from companies array (AI companies only)
      for (let year = yearRange[0]; year <= yearRange[1]; year++) {
        yearData[year] = { total: 0, agents: 0, ai: 0 }
      }
      inRange.forEach((c) => {
        const year = new Date(c.launched_at).getFullYear()
        if (yearData[year]) {
          yearData[year].ai += 1
          if (c.is_agent) {
            yearData[year].agents += 1
          }
        }
      })
    }

    // Domain breakdown
    const domainCounts: Record<string, number> = {}
    agents.forEach((c) => {
      const domain = c.domain || 'other'
      domainCounts[domain] = (domainCounts[domain] || 0) + 1
    })

    const domains = Object.entries(domainCounts)
      .map(([domain, count]) => ({ domain, count }))
      .sort((a, b) => b.count - a.count)

    // Subdomain breakdown
    const subdomainCounts: Record<string, number> = {}
    agents.forEach((c) => {
      if (c.subdomain) {
        subdomainCounts[c.subdomain] = (subdomainCounts[c.subdomain] || 0) + 1
      }
    })

    const subdomains = Object.entries(subdomainCounts)
      .map(([subdomain, count]) => ({ subdomain, count }))
      .sort((a, b) => b.count - a.count)

    // Calculate start and end year percentages (agents / all YC companies)
    const startYear = yearRange[0]
    const endYear = yearRange[1]
    const startData = yearData[startYear]
    const endData = yearData[endYear]

    const startPct =
      startData && startData.total > 0
        ? (startData.agents / startData.total) * 100
        : 0
    const endPct =
      endData && endData.total > 0 ? (endData.agents / endData.total) * 100 : 0
    const percentagePointChange = endPct - startPct

    return {
      totalCompanies: inRange.length,
      agentCompanies: agents.length,
      agentPercentage:
        inRange.length > 0
          ? ((agents.length / inRange.length) * 100).toFixed(1)
          : '0',
      startYearPct: startPct.toFixed(1),
      endYearPct: endPct.toFixed(1),
      percentagePointChange: percentagePointChange.toFixed(1),
      startYear,
      endYear,
      yearData,
      domains,
      subdomains,
    }
  }, [companies, yearRange, yoyTrends])
}
